import { useState, useEffect, useCallback } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { ordersAPI, notificationsAPI, internationalAPI, feedbackAPI } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Separator } from '@/components/ui/separator';
import { toast } from 'sonner';
import {
  ArrowLeft, User, Calendar, Clock, IndianRupee, Package, ChevronRight, ChevronDown, ChevronUp,
  CheckCircle2, Circle, Scissors, HardHat, AlertTriangle, Truck,
  CreditCard, ClipboardCheck, Play, RotateCcw, FileText, Loader2,
  MessageSquare, Zap, Plane, Globe, Copy, ExternalLink, Save, Printer,
} from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import OrderPayments from '@/components/OrderPayments';
import QCHistory from '@/components/QCHistory';
import QCInspectionForm from '@/components/QCInspectionForm';
import CostMarginTab from '@/components/CostMarginTab';
import AlterationsTracker from '@/components/AlterationsTracker';
import WhatsNextBar from '@/components/WhatsNextBar';
import SendNotificationDialog from '@/components/SendNotificationDialog';
import WhatsAppPromptDialog from '@/components/WhatsAppPromptDialog';
import { useAuth } from '@/lib/auth';
import { Breadcrumbs } from '@/components/shared/UXComponents';
import { generateInvoicePDF, downloadPDF } from '@/lib/pdfGenerator';

const STATUS_COLORS = {
  draft: { bg: 'rgba(107,114,128,0.10)', text: '#6B7280', border: 'rgba(107,114,128,0.25)' },
  confirmed: { bg: 'rgba(37,99,235,0.08)', text: '#2563EB', border: 'rgba(37,99,235,0.25)' },
  fabric_ordered: { bg: 'rgba(147,51,234,0.08)', text: '#9333EA', border: 'rgba(147,51,234,0.25)' },
  fabric_received: { bg: 'rgba(6,182,212,0.08)', text: '#0891B2', border: 'rgba(6,182,212,0.25)' },
  in_production: { bg: 'rgba(245,158,11,0.10)', text: '#D97706', border: 'rgba(245,158,11,0.25)' },
  ready_for_trial: { bg: 'rgba(16,185,129,0.08)', text: '#059669', border: 'rgba(16,185,129,0.25)' },
  alterations_in_progress: { bg: 'rgba(245,158,11,0.10)', text: '#D97706', border: 'rgba(245,158,11,0.25)' },
  ready_for_delivery: { bg: 'rgba(16,185,129,0.12)', text: '#047857', border: 'rgba(16,185,129,0.30)' },
  delivered: { bg: 'rgba(27,42,74,0.08)', text: '#1B2A4A', border: 'rgba(27,42,74,0.20)' },
  cancelled: { bg: 'rgba(239,68,68,0.08)', text: '#DC2626', border: 'rgba(239,68,68,0.25)' },
};

const STATUS_LABELS = {
  draft: 'Draft', confirmed: 'Confirmed', fabric_ordered: 'Fabric Ordered',
  fabric_received: 'Fabric Received', in_production: 'In Production',
  ready_for_trial: 'Ready for Trial', alterations_in_progress: 'Alterations in Progress',
  ready_for_delivery: 'Ready for Delivery', delivered: 'Delivered', cancelled: 'Cancelled',
};

const STATUS_ORDER = [
  'confirmed', 'fabric_ordered', 'fabric_received', 'in_production',
  'ready_for_trial', 'alterations_in_progress', 'ready_for_delivery', 'delivered',
];

const ITEM_STATUS_LABELS = {
  pending: 'Pending', pattern_drafted: 'Pattern Drafted', cut: 'Cut',
  in_production: 'In Production', produced: 'Produced', in_trial: 'In Trial',
  in_alteration: 'In Alteration', completed: 'Completed', cancelled: 'Cancelled',
};

const ITEM_STATUS_COLORS = {
  pending: '#6B7280', pattern_drafted: '#9333EA', cut: '#0891B2',
  in_production: '#D97706', produced: '#059669', in_trial: '#2563EB',
  in_alteration: '#D97706', completed: '#1B2A4A', cancelled: '#DC2626',
};

const GARMENT_LABELS = {
  jacket: 'Jacket', trousers: 'Trousers', shirt: 'Shirt', sherwani: 'Sherwani',
  bandhgala: 'Bandhgala', nehru_vest: 'Nehru Vest', kurta: 'Kurta',
  pyjama: 'Pyjama', waistcoat: 'Waistcoat',
};

const CURRENCY_SYMBOLS = { INR: '\u20B9', AED: 'AED ', USD: '$', GBP: '\u00A3' };

function StatusBadge({ status, labels = STATUS_LABELS, colors = STATUS_COLORS }) {
  const c = colors[status] || { bg: 'rgba(107,114,128,0.10)', text: '#6B7280', border: 'rgba(107,114,128,0.25)' };
  return (
    <Badge variant="outline" className="text-[10px] font-semibold px-2 py-0.5 rounded-md whitespace-nowrap"
      style={{ backgroundColor: c.bg, color: c.text, borderColor: c.border }}>
      {labels[status] || status}
    </Badge>
  );
}

const TIMELINE_MILESTONES = [
  { key: 'confirmed', short: 'Confirmed', icon: CheckCircle2 },
  { key: 'fabric_ordered', short: 'Fabric\nOrdered', icon: Package },
  { key: 'fabric_received', short: 'Received', icon: Package },
  { key: 'in_production', short: 'Production', icon: HardHat },
  { key: 'ready_for_trial', short: 'Trial', icon: Scissors },
  { key: 'alterations_in_progress', short: 'Alterations', icon: RotateCcw },
  { key: 'ready_for_delivery', short: 'Delivery', icon: Truck },
  { key: 'delivered', short: 'Delivered', icon: CheckCircle2 },
];

function StatusTimeline({ currentStatus, statusDates, requiredByDate, onAdvance, canEdit }) {
  const currentIdx = STATUS_ORDER.indexOf(currentStatus);
  const isCancelled = currentStatus === 'cancelled';
  const dates = statusDates || {};
  const [confirmTarget, setConfirmTarget] = useState(null);
  const [pastPopover, setPastPopover] = useState(null);
  const [skipWarning, setSkipWarning] = useState(false);

  const isOverdue = requiredByDate && new Date(requiredByDate) < new Date() && currentStatus !== 'delivered' && !isCancelled;

  const formatDate = (iso) => {
    if (!iso) return null;
    const d = new Date(iso);
    return d.toLocaleDateString('en-IN', { day: 'numeric', month: 'short' });
  };

  const formatDateTime = (iso) => {
    if (!iso) return 'Date not recorded';
    const d = new Date(iso);
    return d.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' }) + ' at ' +
      d.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' });
  };

  const handleMilestoneClick = (m, milestoneIdx) => {
    if (isCancelled || !canEdit) return;
    const isPast = currentIdx > milestoneIdx;
    const isCurrent = currentIdx === milestoneIdx;
    const isFuture = currentIdx < milestoneIdx;

    if (isPast) {
      setPastPopover(pastPopover === m.key ? null : m.key);
      setConfirmTarget(null);
    } else if (isFuture) {
      // Check if we're skipping steps
      const expectedNextIdx = currentIdx + 1;
      const isSkipping = milestoneIdx > expectedNextIdx;
      setSkipWarning(isSkipping);
      setConfirmTarget(m.key);
      setPastPopover(null);
    }
  };

  const confirmAdvance = () => {
    if (confirmTarget && onAdvance) {
      onAdvance(confirmTarget);
    }
    setConfirmTarget(null);
    setSkipWarning(false);
  };

  return (
    <div data-testid="order-status-timeline">
      {isOverdue && (
        <div className="flex items-center gap-2 mb-3 px-3 py-2 rounded-lg" style={{ backgroundColor: 'rgba(239,68,68,0.06)', border: '1px solid rgba(239,68,68,0.15)' }}
          data-testid="overdue-warning">
          <AlertTriangle className="w-4 h-4 flex-shrink-0" style={{ color: '#DC2626' }} />
          <p className="text-xs font-medium" style={{ color: '#DC2626' }}>
            Overdue — required by {formatDate(requiredByDate)} but order is still in {STATUS_LABELS[currentStatus]}
          </p>
        </div>
      )}
      <div className="flex items-start gap-0 overflow-x-auto py-1">
        {TIMELINE_MILESTONES.map((m, i) => {
          const milestoneIdx = STATUS_ORDER.indexOf(m.key);
          const isDone = !isCancelled && currentIdx >= milestoneIdx;
          const isCurrent = !isCancelled && currentStatus === m.key;
          const isFuture = !isCancelled && currentIdx < milestoneIdx;
          const dateStr = formatDate(dates[m.key]);
          const Icon = m.icon;

          return (
            <div key={m.key} className="flex items-start flex-shrink-0">
              <div className="flex flex-col items-center relative" style={{ width: 80 }}>
                <button
                  onClick={() => handleMilestoneClick(m, milestoneIdx)}
                  disabled={isCancelled || !canEdit || isCurrent}
                  className="group relative w-9 h-9 rounded-full flex items-center justify-center border-2 transition-all duration-200"
                  title={isFuture && canEdit ? `Click to advance to "${STATUS_LABELS[m.key]}"` : isDone && !isCurrent ? `Reached: ${formatDateTime(dates[m.key])}` : ''}
                  style={{
                    borderColor: isCurrent ? 'var(--brand-gold)' : isDone ? '#059669' : 'rgba(27,42,74,0.12)',
                    backgroundColor: isCurrent ? 'var(--brand-gold)' : isDone ? '#059669' : 'transparent',
                    boxShadow: isCurrent ? '0 0 0 3px rgba(197,165,90,0.2)' : 'none',
                    transform: isCurrent ? 'scale(1.15)' : 'scale(1)',
                    cursor: (isFuture || (isDone && !isCurrent)) && canEdit ? 'pointer' : isCurrent ? 'default' : 'default',
                  }}
                  data-testid={`timeline-milestone-${m.key}`}
                >
                  <Icon className="w-3.5 h-3.5" style={{ color: isDone || isCurrent ? 'white' : 'rgba(27,42,74,0.2)' }} />
                  {isFuture && canEdit && (
                    <span className="absolute -top-1 -right-1 w-3 h-3 rounded-full bg-blue-500 border-2 border-white opacity-0 group-hover:opacity-100 transition-opacity" />
                  )}
                </button>
                <p className="text-[9px] mt-1 text-center leading-tight whitespace-pre-line"
                  style={{ color: isCurrent ? 'var(--brand-navy)' : isDone ? '#059669' : 'var(--ink-muted)', fontWeight: isCurrent ? 700 : isDone ? 500 : 400 }}>
                  {m.short}
                </p>
                {dateStr && (
                  <p className="text-[8px] tabular-nums" style={{ color: isDone ? '#059669' : 'var(--ink-muted)' }}>
                    {dateStr}
                  </p>
                )}
                {/* Past milestone popover */}
                {pastPopover === m.key && dates[m.key] && (
                  <div className="absolute top-11 left-1/2 -translate-x-1/2 z-20 bg-white border rounded-lg shadow-lg p-2.5 min-w-[140px]"
                    style={{ borderColor: 'rgba(27,42,74,0.12)' }} data-testid={`past-popover-${m.key}`}>
                    <p className="text-[10px] font-semibold" style={{ color: 'var(--brand-navy)' }}>{STATUS_LABELS[m.key]}</p>
                    <p className="text-[9px] mt-0.5" style={{ color: 'var(--ink-muted)' }}>
                      {formatDateTime(dates[m.key])}
                    </p>
                    <button onClick={() => setPastPopover(null)} className="absolute top-1 right-1 text-gray-400 hover:text-gray-600">
                      <Circle className="w-3 h-3" />
                    </button>
                  </div>
                )}
              </div>
              {i < TIMELINE_MILESTONES.length - 1 && (
                <div className="w-4 h-0.5 mt-4 rounded-full flex-shrink-0"
                  style={{ backgroundColor: !isCancelled && currentIdx > STATUS_ORDER.indexOf(m.key) ? '#059669' : 'rgba(27,42,74,0.08)' }} />
              )}
            </div>
          );
        })}
      </div>

      {/* Advance Confirmation Dialog */}
      <Dialog open={!!confirmTarget} onOpenChange={(open) => { if (!open) { setConfirmTarget(null); setSkipWarning(false); } }}>
        <DialogContent className="max-w-sm" data-testid="advance-status-dialog">
          <DialogHeader>
            <DialogTitle style={{ color: 'var(--brand-navy)' }}>Advance Order Status</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <p className="text-sm" style={{ color: 'var(--navy-ink)' }}>
              Move order to <strong>"{STATUS_LABELS[confirmTarget]}"</strong>?
            </p>
            {skipWarning && (
              <div className="flex items-start gap-2 p-3 rounded-lg" style={{ backgroundColor: 'rgba(245,158,11,0.08)', border: '1px solid rgba(245,158,11,0.2)' }}
                data-testid="skip-warning">
                <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" style={{ color: '#D97706' }} />
                <div>
                  <p className="text-xs font-semibold" style={{ color: '#D97706' }}>Skipping steps</p>
                  <p className="text-[11px] mt-0.5" style={{ color: '#92400E' }}>
                    Some intermediate steps will be skipped. This is allowed but may not reflect the ideal workflow.
                  </p>
                </div>
              </div>
            )}
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" size="sm" onClick={() => { setConfirmTarget(null); setSkipWarning(false); }}>Cancel</Button>
            <Button size="sm" onClick={confirmAdvance} style={{ backgroundColor: skipWarning ? '#D97706' : 'var(--brand-navy)' }}
              data-testid="confirm-advance-btn">
              {skipWarning ? 'Advance Anyway' : 'Confirm'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

/* ---- Activity Log ---- */
const ACTION_ICONS = {
  status_changed: Play, payment_recorded: CreditCard, payment_reminder: AlertTriangle,
  qc_pre_trial_failed: ClipboardCheck, qc_fabric_failed: AlertTriangle,
  qc_failed: AlertTriangle, notification_sent: MessageSquare,
  order_created: FileText, default: Circle,
};
const ACTION_COLORS = {
  status_changed: '#2563EB', payment_recorded: '#059669', payment_reminder: '#DC2626',
  qc_pre_trial_failed: '#DC2626', qc_fabric_failed: '#DC2626',
  order_created: 'var(--brand-navy)', default: '#6B7280',
};

function ActivityLog({ log }) {
  const sorted = [...(log || [])].sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));
  if (!sorted.length) return <p className="text-xs text-center py-6" style={{ color: 'var(--ink-muted)' }}>No activity yet</p>;

  return (
    <div className="space-y-0" data-testid="activity-log">
      {sorted.map((entry) => {
        const Icon = ACTION_ICONS[entry.action] || ACTION_ICONS.default;
        const color = ACTION_COLORS[entry.action] || ACTION_COLORS.default;
        const dt = new Date(entry.timestamp);
        return (
          <div key={entry.id} className="flex gap-3 py-2 border-b last:border-0" style={{ borderColor: 'rgba(27,42,74,0.04)' }}>
            <div className="mt-0.5 w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0"
              style={{ backgroundColor: color + '12' }}>
              <Icon className="w-2.5 h-2.5" style={{ color }} />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-xs" style={{ color: 'var(--navy-ink)' }}>{entry.notes}</p>
              <p className="text-[9px] mt-0.5" style={{ color: 'var(--ink-muted)' }}>
                {entry.by || 'System'} &middot; {dt.toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })} {dt.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })}
              </p>
            </div>
          </div>
        );
      })}
    </div>
  );
}

/* ---- Collapsible History Link ---- */
function HistoryLink({ activityLog }) {
  const [expanded, setExpanded] = useState(false);
  const sorted = [...(activityLog || [])].sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));
  if (!sorted.length) return null;

  return (
    <div data-testid="order-history-link">
      <button onClick={() => setExpanded(!expanded)}
        className="flex items-center gap-1.5 text-xs font-medium py-2 transition-colors hover:underline"
        style={{ color: 'var(--ink-muted)' }}
        data-testid="toggle-history-btn">
        <Clock className="w-3 h-3" />
        History ({sorted.length} events)
        {expanded ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
      </button>
      {expanded && (
        <Card className="rounded-xl border border-border/80 bg-white shadow-sm mt-1">
          <CardContent className="p-4">
            <div className="space-y-2 max-h-[300px] overflow-y-auto pr-1">
              {sorted.map((entry) => {
                const Icon = ACTION_ICONS[entry.action] || ACTION_ICONS.default;
                const color = ACTION_COLORS[entry.action] || ACTION_COLORS.default;
                const dt = new Date(entry.timestamp);
                return (
                  <div key={entry.id} className="flex gap-3 py-1.5 border-b last:border-0" style={{ borderColor: 'rgba(27,42,74,0.04)' }}>
                    <div className="mt-0.5 w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0"
                      style={{ backgroundColor: color + '12' }}>
                      <Icon className="w-2.5 h-2.5" style={{ color }} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-xs" style={{ color: 'var(--navy-ink)' }}>{entry.notes}</p>
                      <p className="text-[9px] mt-0.5" style={{ color: 'var(--ink-muted)' }}>
                        {entry.by || 'System'} &middot; {dt.toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })} {dt.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })}
                      </p>
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

/* ---- Delivery Dialog ---- */
function DeliveryDialog({ open, onClose, onDeliver }) {
  const [form, setForm] = useState({
    delivery_date: new Date().toISOString().split('T')[0],
    delivery_method: 'pickup',
    received_by: '',
    notes: '',
    tracking_number: '',
  });
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async () => {
    setSubmitting(true);
    await onDeliver(form);
    setSubmitting(false);
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-md" data-testid="delivery-dialog">
        <DialogHeader>
          <DialogTitle style={{ color: 'var(--brand-navy)' }}>Mark as Delivered</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="text-xs">Delivery Date</Label>
            <Input type="date" value={form.delivery_date}
              onChange={e => setForm(p => ({ ...p, delivery_date: e.target.value }))}
              className="mt-1 h-9" data-testid="delivery-date-input" />
          </div>
          <div>
            <Label className="text-xs">Method</Label>
            <Select value={form.delivery_method} onValueChange={v => setForm(p => ({ ...p, delivery_method: v }))}>
              <SelectTrigger className="mt-1 h-9" data-testid="delivery-method-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="pickup">Client Pickup</SelectItem>
                <SelectItem value="courier">Courier</SelectItem>
                <SelectItem value="hand_delivered">Hand Delivered</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">Received By</Label>
            <Input value={form.received_by}
              onChange={e => setForm(p => ({ ...p, received_by: e.target.value }))}
              placeholder="Name of person receiving" className="mt-1 h-9" data-testid="received-by-input" />
          </div>
          {form.delivery_method === 'courier' && (
            <div>
              <Label className="text-xs">Tracking Number</Label>
              <Input value={form.tracking_number}
                onChange={e => setForm(p => ({ ...p, tracking_number: e.target.value }))}
                placeholder="Courier tracking #" className="mt-1 h-9" data-testid="tracking-input" />
            </div>
          )}
          <div>
            <Label className="text-xs">Notes</Label>
            <Textarea value={form.notes}
              onChange={e => setForm(p => ({ ...p, notes: e.target.value }))}
              placeholder="Delivery notes..." className="mt-1" rows={2} data-testid="delivery-notes-input" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" size="sm" onClick={onClose}>Cancel</Button>
          <Button size="sm" onClick={handleSubmit} disabled={submitting} className="gap-1"
            style={{ backgroundColor: 'var(--brand-navy)' }} data-testid="confirm-delivery-btn">
            {submitting ? <Loader2 className="w-3 h-3 animate-spin" /> : <Truck className="w-3 h-3" />}
            Confirm Delivery
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function OrderDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [statusUpdating, setStatusUpdating] = useState(false);
  const [activeTab, setActiveTab] = useState('details');
  const [deliveryOpen, setDeliveryOpen] = useState(false);
  const [notifDialogOpen, setNotifDialogOpen] = useState(false);
  const [pendingNotification, setPendingNotification] = useState(null);
  const [feedback, setFeedback] = useState(null);

  const fetchOrder = useCallback(async () => {
    try {
      const res = await ordersAPI.get(id);
      setOrder(res.data);
      // Fetch feedback if order is delivered
      if (res.data?.status === 'delivered') {
        try {
          const fbRes = await feedbackAPI.forOrder(id);
          setFeedback(fbRes.data);
        } catch { /* No feedback yet */ }
      }
    } catch (err) {
      console.error('Failed to fetch order', err);
      toast.error('Order not found');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { fetchOrder(); }, [fetchOrder]);

  const handleStatusChange = async (newStatus) => {
    setStatusUpdating(true);
    try {
      const res = await ordersAPI.updateStatus(id, { status: newStatus });
      setOrder(res.data);
      toast.success(`Status updated to ${STATUS_LABELS[newStatus]}`);
      // Check if a notification was auto-triggered
      if (res.data?.pending_notification) {
        setPendingNotification(res.data.pending_notification);
      }
    } catch (err) {
      toast.error('Failed to update status');
    } finally {
      setStatusUpdating(false);
    }
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-10 w-48" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  if (!order) {
    return (
      <div className="text-center py-20">
        <p className="text-lg font-medium" style={{ color: 'var(--navy-ink)' }}>Order not found</p>
        <Link to="/app/orders"><Button variant="outline" className="mt-4">Back to Orders</Button></Link>
      </div>
    );
  }

  const cs = CURRENCY_SYMBOLS[order.currency] || '';

  return (
    <div className="space-y-6" data-testid="order-detail-page">
      {/* Breadcrumbs */}
      <Breadcrumbs items={[
        { label: 'Orders', href: '/app/orders' },
        { label: order.order_number },
      ]} />

      {/* Header */}
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <div className="flex items-center gap-3">
            <h1 className="text-2xl sm:text-3xl font-semibold" style={{ color: 'var(--brand-navy)' }} data-testid="order-number-heading">
              {order.order_number}
            </h1>
            <StatusBadge status={order.status} />
            {order.is_express && (
              <Badge className="text-[10px] px-2 py-0.5 font-bold gap-1 rounded-md"
                style={{ backgroundColor: 'rgba(217,119,6,0.10)', color: '#D97706', border: '1px solid rgba(217,119,6,0.3)' }}
                data-testid="express-badge">
                <Zap className="w-3 h-3" /> EXPRESS
              </Badge>
            )}
          </div>
          <div className="flex items-center gap-4 mt-1.5 text-sm flex-wrap" style={{ color: 'var(--ink-muted)' }}>
            <Link to={`/app/clients/${order.client_id}`} className="flex items-center gap-1 hover:underline" style={{ color: 'var(--brand-navy)' }}
              data-testid="order-client-link">
              <User className="w-3.5 h-3.5" /> {order.client_name}
            </Link>
            {order.stylist_name && (
              <span className="flex items-center gap-1" data-testid="order-stylist-name" title="Stylist / Salesperson">
                <User className="w-3.5 h-3.5" style={{ color: 'var(--brand-gold)' }} /> Stylist: {order.stylist_name}
              </span>
            )}
            <span className="flex items-center gap-1"><Calendar className="w-3.5 h-3.5" /> {order.order_date}</span>
            {order.required_by_date && (
              <span className="flex items-center gap-1"><Clock className="w-3.5 h-3.5" /> Due: {order.required_by_date}</span>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" className="gap-1.5 text-xs h-9 rounded-lg no-print"
            onClick={() => {
              const doc = generateInvoicePDF(order);
              downloadPDF(doc, `Invoice-${order.order_number || order.id?.slice(-8)}.pdf`);
              toast.success('Invoice PDF downloaded');
            }} data-testid="print-order-btn">
            <Printer className="w-3.5 h-3.5" /> Download Invoice
          </Button>
          <Button variant="outline" size="sm" className="gap-1.5 text-xs h-9 rounded-lg no-print"
            style={{ color: '#25D366', borderColor: '#25D366' }}
            onClick={() => setNotifDialogOpen(true)} data-testid="send-notification-btn">
            <MessageSquare className="w-3.5 h-3.5" /> Notify
          </Button>
          <Select value={order.status} onValueChange={handleStatusChange} disabled={statusUpdating}>
            <SelectTrigger className="h-9 w-[180px] rounded-lg text-sm no-print" data-testid="order-status-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {Object.entries(STATUS_LABELS).map(([k, v]) => (
                <SelectItem key={k} value={k}>{v}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Print Styles */}
      <style>{`
        @media print {
          body * { visibility: hidden; }
          [data-testid="order-detail-page"], [data-testid="order-detail-page"] * { visibility: visible; }
          [data-testid="order-detail-page"] { position: absolute; left: 0; top: 0; width: 100%; padding: 20px; }
          .no-print, [data-testid="order-detail-tabs"], [data-testid="order-status-select"] { display: none !important; }
          @page { margin: 1cm; }
        }
      `}</style>

      {/* Status Timeline */}
      <Card className="rounded-xl border border-border/80 bg-white shadow-[0_1px_0_rgba(16,24,40,0.04),0_10px_24px_rgba(16,24,40,0.06)]">
        <CardContent className="p-4">
          <StatusTimeline
            currentStatus={order.status}
            statusDates={order.status_dates}
            requiredByDate={order.required_by_date}
            onAdvance={handleStatusChange}
            canEdit={user?.role === 'Admin' || user?.role === 'Sales'}
          />
        </CardContent>
      </Card>

      {/* Customer Feedback (for delivered orders) */}
      {order.status === 'delivered' && (
        <Card className="rounded-xl border border-border/80 bg-white shadow-sm" data-testid="order-feedback-card">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <h3 className="text-sm font-semibold flex items-center gap-2" style={{ color: 'var(--brand-navy)' }}>
                <MessageSquare className="w-4 h-4" /> Customer Feedback
              </h3>
              {!feedback && (
                <Button variant="outline" size="sm" className="text-xs h-7" asChild>
                  <a href={`/feedback/${order.id}`} target="_blank" rel="noopener noreferrer">
                    <ExternalLink className="w-3 h-3 mr-1" /> Feedback Link
                  </a>
                </Button>
              )}
            </div>
            {feedback ? (
              <div className="mt-3">
                <div className="flex items-center gap-1" data-testid="feedback-rating-display">
                  {[1, 2, 3, 4, 5].map((n) => (
                    <svg key={n} className="w-5 h-5" viewBox="0 0 24 24" fill={n <= feedback.rating ? '#F59E0B' : 'none'}
                      stroke={n <= feedback.rating ? '#F59E0B' : '#D1D5DB'} strokeWidth="2">
                      <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
                    </svg>
                  ))}
                  <span className="ml-2 text-sm font-medium" style={{ color: 'var(--navy-ink)' }}>{feedback.rating}/5</span>
                </div>
                {feedback.comment && (
                  <p className="mt-2 text-sm p-3 rounded-lg" style={{ backgroundColor: 'rgba(27,42,74,0.03)', color: 'var(--navy-ink)' }}
                    data-testid="feedback-comment-display">
                    "{feedback.comment}"
                  </p>
                )}
                <p className="mt-2 text-[10px]" style={{ color: 'var(--ink-muted)' }}>
                  Submitted {new Date(feedback.created_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}
                </p>
              </div>
            ) : (
              <p className="mt-2 text-xs" style={{ color: 'var(--ink-muted)' }}>
                No feedback received yet. The client will receive a feedback request 7 days after delivery.
              </p>
            )}
          </CardContent>
        </Card>
      )}

      {/* What's Next Bar */}
      <WhatsNextBar order={order} onRefresh={fetchOrder} />

      {/* Tab Navigation */}
      <div className="flex gap-1 border-b" style={{ borderColor: 'rgba(27,42,74,0.08)' }} data-testid="order-detail-tabs">
        {[
          { key: 'details', label: 'Details' },
          ...(order.service_mode === 'international' ? [{ key: 'shipping', label: 'Shipping' }] : []),
          ...(user?.role === 'Admin' ? [{ key: 'cost', label: 'Cost & Margin' }] : []),
          { key: 'payments', label: 'Payments' },
          { key: 'alterations', label: 'Alterations' },
          { key: 'qc', label: 'Quality Control' },
          { key: 'activity', label: 'Activity Log' },
        ].map(tab => (
          <button key={tab.key} onClick={() => setActiveTab(tab.key)}
            className="px-4 py-2 text-xs font-semibold transition-colors relative"
            style={{
              color: activeTab === tab.key ? 'var(--brand-navy)' : 'var(--ink-muted)',
              borderBottom: activeTab === tab.key ? '2px solid var(--brand-navy)' : '2px solid transparent',
            }}
            data-testid={`order-tab-${tab.key}`}>
            {tab.label}
          </button>
        ))}
      </div>

      {/* Tab: Shipping (International Orders) */}
      {activeTab === 'shipping' && order.service_mode === 'international' && (
        <InternationalShippingTab orderId={order.id} orderCurrency={order.currency} onUpdate={fetchOrder} />
      )}

      {/* Tab: Details */}
      {activeTab === 'details' && (
        <>
      {/* Garment Items */}
      <div>
        <h3 className="text-sm font-semibold mb-3" style={{ color: 'var(--brand-navy)' }}>
          Garments ({order.items?.length || 0})
        </h3>
        <div className="grid gap-3">
          {(order.items || []).map((item) => (
            <Card key={item.id} className="rounded-xl border border-border/80 bg-white shadow-sm" data-testid={`order-item-${item.id}`}>
              <CardContent className="p-4">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex gap-4 items-start flex-1">
                    {/* Fabric swatch */}
                    {item.fabric_swatch_url ? (
                      <div className="w-16 h-16 rounded-lg overflow-hidden flex-shrink-0 border" style={{ borderColor: 'rgba(27,42,74,0.1)' }}>
                        <img src={item.fabric_swatch_url} alt="" className="w-full h-full object-cover" />
                      </div>
                    ) : (
                      <div className="w-16 h-16 rounded-lg flex-shrink-0 flex items-center justify-center" style={{ backgroundColor: 'rgba(27,42,74,0.04)' }}>
                        <Scissors className="w-6 h-6 opacity-20" />
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <p className="text-sm font-semibold" style={{ color: 'var(--navy-ink)' }}>
                          {GARMENT_LABELS[item.garment_type] || item.garment_type}
                        </p>
                        <Badge variant="outline" className="text-[9px] px-1.5 py-0 rounded-md"
                          style={{ color: ITEM_STATUS_COLORS[item.status] || '#6B7280' }}>
                          {ITEM_STATUS_LABELS[item.status] || item.status}
                        </Badge>
                      </div>
                      {item.fabric_name && (
                        <p className="text-xs mt-0.5" style={{ color: 'var(--ink-muted)' }}>
                          Fabric: {item.fabric_name} &middot; {item.fabric_quantity_metres}m
                        </p>
                      )}
                      {item.assigned_worker_name && (
                        <p className="text-xs mt-0.5 flex items-center gap-1" style={{ color: 'var(--ink-muted)' }}>
                          <HardHat className="w-3 h-3" /> {item.assigned_worker_name}
                        </p>
                      )}
                      {item.notes && (
                        <p className="text-xs mt-1 italic" style={{ color: 'var(--ink-muted)' }}>{item.notes}</p>
                      )}
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>

      {/* Total Price + Payment Status */}
      <Card className="rounded-xl border border-border/80 bg-white shadow-sm" data-testid="order-total-card">
        <CardContent className="p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs font-medium uppercase tracking-wider" style={{ color: 'var(--ink-muted)' }}>Total</p>
              <p className="text-2xl font-bold tabular-nums" style={{ color: 'var(--brand-navy)' }}>
                {cs}{order.total_price?.toLocaleString('en-IN')}
              </p>
              {order.is_express && order.express_surcharge > 0 && (
                <p className="text-[10px] flex items-center gap-1 mt-0.5" style={{ color: '#D97706' }}>
                  <Zap className="w-3 h-3" /> Includes express surcharge of {cs}{order.express_surcharge?.toLocaleString('en-IN')}
                </p>
              )}
            </div>
            <div className="text-right">
              <p className="text-xs font-medium uppercase tracking-wider" style={{ color: 'var(--ink-muted)' }}>Payment</p>
              <StatusBadge status={order.status} />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Notes */}
      {order.notes && (
        <Card className="rounded-xl border border-border/80 bg-white">
          <CardContent className="p-4">
            <p className="text-xs font-semibold uppercase tracking-wide mb-1" style={{ color: 'var(--ink-muted)' }}>Notes</p>
            <p className="text-sm" style={{ color: 'var(--navy-ink)' }}>{order.notes}</p>
          </CardContent>
        </Card>
      )}

      {/* Collapsible History */}
      <HistoryLink activityLog={order.activity_log} />
        </>
      )}

      {/* Tab: Cost & Margin */}
      {activeTab === 'cost' && user?.role === 'Admin' && (
        <CostMarginTab orderId={id} />
      )}

      {/* Tab: Payments */}
      {activeTab === 'payments' && (
        <div>
          <h3 className="text-sm font-semibold mb-3" style={{ color: 'var(--brand-navy)' }}>Payments</h3>
          <OrderPayments orderId={id} orderCurrency={order.currency} totalPrice={order.total_price} />
        </div>
      )}

      {/* Tab: Alterations */}
      {activeTab === 'alterations' && (
        <AlterationsTracker
          orderId={id}
          orderItems={order.items}
          orderStatus={order.status}
          onRefresh={fetchOrder}
        />
      )}

      {/* Tab: QC */}
      {activeTab === 'qc' && (
        <div>
          <h3 className="text-sm font-semibold mb-3" style={{ color: 'var(--brand-navy)' }}>Quality Control</h3>
          <QCHistory orderId={id} />
        </div>
      )}

      {/* Tab: Activity Log */}
      {activeTab === 'activity' && (
        <Card className="rounded-xl border bg-white">
          <CardHeader className="pb-1 pt-3 px-4">
            <CardTitle className="text-sm font-semibold" style={{ color: 'var(--brand-navy)' }}>Activity Log</CardTitle>
          </CardHeader>
          <CardContent className="px-4 pb-4">
            <ActivityLog log={order.activity_log} />
          </CardContent>
        </Card>
      )}

      {/* Delivery Dialog */}
      <DeliveryDialog
        open={deliveryOpen}
        onClose={() => setDeliveryOpen(false)}
        onDeliver={async (form) => {
          try {
            const res = await ordersAPI.deliver(id, form);
            setOrder(res.data);
            setDeliveryOpen(false);
            toast.success('Order marked as delivered!');
          } catch (err) {
            toast.error(err.response?.data?.detail || 'Delivery failed');
          }
        }}
      />

      {/* Send Notification Dialog */}
      <SendNotificationDialog
        open={notifDialogOpen}
        onOpenChange={setNotifDialogOpen}
        orderId={id}
        clientId={order.client_id}
      />

      {/* WhatsApp Prompt (auto-triggered on status change) */}
      {pendingNotification && (
        <WhatsAppPromptDialog
          notification={pendingNotification}
          onClose={() => setPendingNotification(null)}
        />
      )}
    </div>
  );
}


/* ============================================================
   International Shipping Tab Component
   ============================================================ */
const SHIPPING_PROVIDERS = ['DHL', 'FedEx', 'Aramex', 'BlueDart', 'Other'];
const SHIPPING_STATUS_LABELS = {
  pending: 'Pending', shipped: 'Shipped', in_transit: 'In Transit',
  out_for_delivery: 'Out for Delivery', delivered: 'Delivered', exception: 'Exception',
};
const SHIPPING_STATUS_COLORS = {
  pending: { bg: 'rgba(107,114,128,0.08)', text: '#6B7280' },
  shipped: { bg: 'rgba(59,130,246,0.08)', text: '#2563EB' },
  in_transit: { bg: 'rgba(245,158,11,0.08)', text: '#D97706' },
  out_for_delivery: { bg: 'rgba(16,185,129,0.08)', text: '#059669' },
  delivered: { bg: 'rgba(16,185,129,0.12)', text: '#047857' },
  exception: { bg: 'rgba(239,68,68,0.08)', text: '#DC2626' },
};

function InternationalShippingTab({ orderId, orderCurrency, onUpdate }) {
  const [shipping, setShipping] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    shipping_provider: '', shipping_provider_other: '', tracking_number: '',
    shipping_cost: 0, customs_duty_estimate: 0, shipping_date: '', expected_delivery_date: '',
    shipping_status: 'pending', shipping_notes: '',
  });

  useEffect(() => {
    internationalAPI.getOrderShipping(orderId)
      .then(res => {
        const data = res.data || {};
        setShipping(data);
        setForm({
          shipping_provider: data.shipping_provider || '',
          shipping_provider_other: data.shipping_provider_other || '',
          tracking_number: data.tracking_number || '',
          shipping_cost: data.shipping_cost || 0,
          customs_duty_estimate: data.customs_duty_estimate || 0,
          shipping_date: data.shipping_date || '',
          expected_delivery_date: data.expected_delivery_date || '',
          shipping_status: data.shipping_status || 'pending',
          shipping_notes: data.shipping_notes || '',
        });
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, [orderId]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await internationalAPI.updateOrderShipping(orderId, form);
      toast.success('Shipping details saved');
      onUpdate?.();
    } catch {
      toast.error('Failed to save');
    }
    setSaving(false);
  };

  const copyTrackingLink = () => {
    const links = {
      DHL: `https://www.dhl.com/en/express/tracking.html?AWB=${form.tracking_number}`,
      FedEx: `https://www.fedex.com/fedextrack/?trknbr=${form.tracking_number}`,
      Aramex: `https://www.aramex.com/track/results?ShipmentNumber=${form.tracking_number}`,
      BlueDart: `https://www.bluedart.com/tracking?tracknumbers=${form.tracking_number}`,
    };
    const link = links[form.shipping_provider] || form.tracking_number;
    navigator.clipboard.writeText(link);
    toast.success('Tracking link copied');
  };

  if (loading) return <Skeleton className="h-48 w-full" />;

  const statusStyle = SHIPPING_STATUS_COLORS[form.shipping_status] || SHIPPING_STATUS_COLORS.pending;
  const rate = shipping?.exchange_rate || 1;
  const showInr = orderCurrency && orderCurrency !== 'INR';

  return (
    <div className="space-y-4" data-testid="shipping-tab">
      {/* Shipping Status Banner */}
      <div className="flex items-center justify-between p-4 rounded-xl" style={{ backgroundColor: statusStyle.bg }}>
        <div className="flex items-center gap-3">
          <Plane className="w-6 h-6" style={{ color: statusStyle.text }} />
          <div>
            <p className="text-sm font-bold" style={{ color: statusStyle.text }}>
              {SHIPPING_STATUS_LABELS[form.shipping_status] || 'Pending'}
            </p>
            {form.tracking_number && (
              <p className="text-xs" style={{ color: 'var(--ink-muted)' }}>
                {form.shipping_provider} - {form.tracking_number}
              </p>
            )}
          </div>
        </div>
        {form.tracking_number && (
          <Button variant="outline" size="sm" className="h-8 text-[11px] gap-1" onClick={copyTrackingLink}
            data-testid="copy-tracking-btn">
            <Copy className="w-3 h-3" /> Copy Tracking Link
          </Button>
        )}
      </div>

      {/* Shipping Form */}
      <Card className="rounded-xl border border-border/80 bg-white shadow-sm">
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <CardTitle className="text-sm font-semibold flex items-center gap-2" style={{ color: 'var(--brand-navy)' }}>
              <Globe className="w-4 h-4" style={{ color: '#2563EB' }} />
              Shipping Details
            </CardTitle>
            <Button size="sm" className="gap-1 rounded-lg text-xs" style={{ backgroundColor: 'var(--brand-navy)' }}
              onClick={handleSave} disabled={saving} data-testid="save-shipping-btn">
              {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : <Save className="w-3 h-3" />} Save
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Provider & Tracking */}
          <div className="grid grid-cols-3 gap-4">
            <div>
              <Label className="text-xs font-medium" style={{ color: 'var(--ink-muted)' }}>Shipping Provider</Label>
              <Select value={form.shipping_provider} onValueChange={v => setForm(prev => ({ ...prev, shipping_provider: v }))}>
                <SelectTrigger className="h-9 mt-1" data-testid="shipping-provider-select">
                  <SelectValue placeholder="Select provider" />
                </SelectTrigger>
                <SelectContent>
                  {SHIPPING_PROVIDERS.map(p => <SelectItem key={p} value={p}>{p}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            {form.shipping_provider === 'Other' && (
              <div>
                <Label className="text-xs font-medium" style={{ color: 'var(--ink-muted)' }}>Provider Name</Label>
                <Input value={form.shipping_provider_other} className="h-9 mt-1"
                  onChange={e => setForm(prev => ({ ...prev, shipping_provider_other: e.target.value }))} />
              </div>
            )}
            <div className={form.shipping_provider === 'Other' ? '' : 'col-span-2'}>
              <Label className="text-xs font-medium" style={{ color: 'var(--ink-muted)' }}>Tracking Number</Label>
              <Input value={form.tracking_number} className="h-9 mt-1" placeholder="AWB / Tracking #"
                onChange={e => setForm(prev => ({ ...prev, tracking_number: e.target.value }))}
                data-testid="tracking-number-input" />
            </div>
          </div>

          {/* Dates */}
          <div className="grid grid-cols-3 gap-4">
            <div>
              <Label className="text-xs font-medium" style={{ color: 'var(--ink-muted)' }}>Shipping Date</Label>
              <Input type="date" value={form.shipping_date} className="h-9 mt-1"
                onChange={e => setForm(prev => ({ ...prev, shipping_date: e.target.value }))}
                data-testid="shipping-date-input" />
            </div>
            <div>
              <Label className="text-xs font-medium" style={{ color: 'var(--ink-muted)' }}>Expected Delivery</Label>
              <Input type="date" value={form.expected_delivery_date} className="h-9 mt-1"
                onChange={e => setForm(prev => ({ ...prev, expected_delivery_date: e.target.value }))}
                data-testid="expected-delivery-input" />
            </div>
            <div>
              <Label className="text-xs font-medium" style={{ color: 'var(--ink-muted)' }}>Shipping Status</Label>
              <Select value={form.shipping_status} onValueChange={v => setForm(prev => ({ ...prev, shipping_status: v }))}>
                <SelectTrigger className="h-9 mt-1" data-testid="shipping-status-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(SHIPPING_STATUS_LABELS).map(([k, v]) => (
                    <SelectItem key={k} value={k}>{v}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Costs */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="text-xs font-medium" style={{ color: 'var(--ink-muted)' }}>
                Shipping Cost ({orderCurrency || 'INR'})
              </Label>
              <Input type="number" value={form.shipping_cost} className="h-9 mt-1"
                onChange={e => setForm(prev => ({ ...prev, shipping_cost: parseFloat(e.target.value) || 0 }))}
                data-testid="shipping-cost-input" />
              {showInr && form.shipping_cost > 0 && (
                <p className="text-[10px] mt-1" style={{ color: 'var(--ink-muted)' }}>
                  ≈ ₹{(form.shipping_cost * rate).toLocaleString('en-IN', { maximumFractionDigits: 0 })} INR
                </p>
              )}
            </div>
            <div>
              <Label className="text-xs font-medium" style={{ color: 'var(--ink-muted)' }}>
                Customs Duty Estimate ({orderCurrency || 'INR'})
              </Label>
              <Input type="number" value={form.customs_duty_estimate} className="h-9 mt-1"
                onChange={e => setForm(prev => ({ ...prev, customs_duty_estimate: parseFloat(e.target.value) || 0 }))}
                data-testid="customs-duty-input" />
              {showInr && form.customs_duty_estimate > 0 && (
                <p className="text-[10px] mt-1" style={{ color: 'var(--ink-muted)' }}>
                  ≈ ₹{(form.customs_duty_estimate * rate).toLocaleString('en-IN', { maximumFractionDigits: 0 })} INR
                </p>
              )}
            </div>
          </div>

          {/* Notes */}
          <div>
            <Label className="text-xs font-medium" style={{ color: 'var(--ink-muted)' }}>Shipping Notes</Label>
            <Textarea value={form.shipping_notes} rows={2} className="mt-1 text-xs"
              placeholder="Any special instructions or notes..."
              onChange={e => setForm(prev => ({ ...prev, shipping_notes: e.target.value }))} />
          </div>
        </CardContent>
      </Card>

      {/* Cost Summary */}
      <Card className="rounded-xl border border-border/80 bg-white shadow-sm">
        <CardContent className="p-4">
          <div className="flex items-center justify-between">
            <p className="text-sm font-medium" style={{ color: 'var(--navy-ink)' }}>Total Shipping + Customs</p>
            <div className="text-right">
              <p className="text-lg font-bold" style={{ color: 'var(--brand-navy)' }}>
                {orderCurrency || '₹'} {(form.shipping_cost + form.customs_duty_estimate).toLocaleString('en-IN', { maximumFractionDigits: 0 })}
              </p>
              {showInr && (
                <p className="text-xs" style={{ color: 'var(--ink-muted)' }}>
                  ≈ ₹{((form.shipping_cost + form.customs_duty_estimate) * rate).toLocaleString('en-IN', { maximumFractionDigits: 0 })} INR
                </p>
              )}
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
